import { Box, Button, ListItemButton, ListItemText, Typography } from '@mui/material';
import List from '@mui/material/List';
import { parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { t } from 'i18next';
import React from 'react';
import { Link } from 'react-router-dom';

import { scheduleEnd, scheduleStart } from '@sophocles/entities/RelaySchedule';
import { userPermCheck } from '@sophocles/utils/userPermissionUtils';
import { buildDayPillBooleans, DayPills } from '@sophocles/views/sites/components/RelayCards/dayPills';
import ScheduleListItem from '@sophocles/views/sites/components/RelayCards/ScheduleListItem';
import ScheduleTimeline from '@sophocles/views/sites/components/RelayCards/ScheduleTimeline';

class ScheduleList extends React.Component {
  constructor(props) {
    super(props);
    this.state = { open: false };
  }

  handleRequestClose = () => {
    this.setState({
      open: false,
    });
  };

  render() {
    const {
      relay,
      schedules,
      user,
      params,
      modelId,
      isBillboard,
      usesCalendar,
      dimmerAvailable,
      curfew,
      onClickEditSchedule,
      supportsDayOfWeek = false,
      siteTimeZone = 'UTC',
      nextSunset = null,
      nextSunrise = null,
      scheduleView = 'list',
      schedulingEnabled = true,
      normallyClosed = false,
    } = this.props;

    const canEditSchedules = userPermCheck(user, 'pliny.change_relayschedule');
    const dayOfWeekScheduling = supportsDayOfWeek;
    // All schedules render — disabled ones included (shown as "N. Disabled") —
    // so the card keeps a consistent height regardless of how many are active.
    const sortedSchedules = [...schedules].sort((a, b) => (a.schedule_number < b.schedule_number ? -1 : 1));
    const activeSchedules = sortedSchedules.filter(schedule => schedule.enabled);

    const relay_id = relay.id ?? 0;

    const timeZone = siteTimeZone;

    const next_sunset = nextSunset != null ? toZonedTime(parseISO(nextSunset), timeZone) : null;
    const next_sunrise = nextSunrise != null ? toZonedTime(parseISO(nextSunrise), timeZone) : null;

    const scheduleList = sortedSchedules.map(schedule => {
      let scheduleNum = schedule.schedule_number;
      const enabled = schedule.enabled ?? false;
      const alwaysOn = schedule.always_on ?? false;
      const daysOfWeek = schedule.days_of_week ?? null;
      const onTime = schedule.on_time ?? null;
      const onType = schedule.on_type ?? null;
      const offTime = schedule.off_time ?? null;
      const offType = schedule.off_type ?? null;
      const onOffset = schedule.on_offset ?? null;
      const offOffset = schedule.off_offset ?? null;
      const dutyCycle = schedule.duty_cycle ?? 100;

      // Build day pills for day-of-week schedules. Always build for DOW-capable
      // units so all 7 pills render (inactive days shown greyed rather than
      // omitted). Use 0 when daysOfWeek is null — buildDayPillBooleans(null)
      // would treat null as ALL_DAYS_MASK and light every day active.
      const dayPillBooleans = dayOfWeekScheduling ? buildDayPillBooleans(daysOfWeek ?? 0) : null;

      let primaryText = null;
      let secondaryText = null;
      let alwaysOnText = scheduleNum + `. ${t('sites.alwaysOn')}`;
      if (dimmerAvailable) {
        alwaysOnText = (
          <>
            <span>
              {scheduleNum}. {t('sites.alwaysOn')}
            </span>
            <br />
            <span>{t('sites.brightnessPercent', 'Brightness (%): ') + dutyCycle}</span>
          </>
        );
      }

      if (!enabled) {
        // Disabled rows read as background context — fade the label and skip
        // the day pills entirely so active schedules stand out.
        secondaryText = (
          <span
            className="schedule-list-secondary-text"
            style={{ display: 'flex', flexDirection: 'column', opacity: 0.6 }}
          >
            <span>
              <span style={{ fontWeight: 'bold' }}>{scheduleNum}. </span>
              {t('general.disabled', 'Disabled')}
            </span>
          </span>
        );
      } else if (alwaysOn) {
        secondaryText = alwaysOnText;
      } else if (dayOfWeekScheduling) {
        secondaryText = (
          <span className="schedule-list-secondary-text" style={{ display: 'flex', flexDirection: 'column' }}>
            <span>
              <span style={{ fontWeight: 'bold' }}>{scheduleNum}. </span>
              {scheduleStart(alwaysOn, onTime, onType, onOffset, next_sunrise, next_sunset, user, t)}
              {' - '}
              {scheduleEnd(alwaysOn, offTime, offType, offOffset, next_sunrise, next_sunset, user, t)}
            </span>
            {dimmerAvailable ? <span>{t('sites.brightnessPercent', 'Brightness (%): ') + dutyCycle}</span> : null}
            {dayPillBooleans ? <DayPills days={dayPillBooleans} /> : null}
          </span>
        );
      } else {
        secondaryText = (
          <span
            className="schedule-list-secondary-text"
            style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start' }}
          >
            <span style={{ display: 'flex', flexDirection: 'column' }}>
              <span>
                <span style={{ fontWeight: 'bold' }}>{scheduleNum}. </span>
                {scheduleStart(alwaysOn, onTime, onType, onOffset, next_sunrise, next_sunset, user, t)}
                {' - '}
                {scheduleEnd(alwaysOn, offTime, offType, offOffset, next_sunrise, next_sunset, user, t)}
              </span>
              {dimmerAvailable ? <span>{t('sites.brightnessPercent', 'Brightness (%): ') + dutyCycle}</span> : null}
            </span>
          </span>
        );
      }

      return (
        <ScheduleListItem
          key={schedule.id}
          schedule={schedule}
          disabled={usesCalendar || !canEditSchedules}
          primaryText={primaryText}
          secondaryText={secondaryText}
          modelId={modelId}
          isBillboard={isBillboard}
          canEdit={canEditSchedules}
          curfew={curfew}
          user={user}
          onClick={() => onClickEditSchedule(schedule.id)}
        />
      );
    });

    const calendarUrl = `/c/${params.cust_id}/plant/${params.sitegroup_id}/calendar?r=${relay_id}`;

    const hasSchedules = scheduleList.length > 0;
    const hasActiveSchedules = activeSchedules.length > 0;

    const noSchedules = (
      <Box sx={{ px: 2, pb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('sites.noSchedules', 'No Schedules')}
        </Typography>
        <Button size="small" onClick={() => onClickEditSchedule(null)} sx={{ textTransform: 'none', flexShrink: 0 }}>
          {t('actions.configure', 'Configure')}
        </Button>
      </Box>
    );

    const normallyClosedMessage = (
      <ListItemButton onClick={() => onClickEditSchedule(null)} sx={{ px: 2, py: 1 }}>
        <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 500 }}>
          {t('sites.normallyClosedSchedulingDisabled')}
        </Typography>
      </ListItemButton>
    );

    const calendarMessage = (
      <Link to={calendarUrl}>
        <ListItemButton>
          <ListItemText
            primary={t('sites.usesCalendarScheduling', {
              entityType: isBillboard ? t('siteGroups.structure') : t('siteGroups.site'),
            })}
            secondary={t('sites.clickToViewCalendar')}
          />
        </ListItemButton>
      </Link>
    );

    return (
      <List sx={{ p: 0 }}>
        {usesCalendar ? (
          calendarMessage
        ) : normallyClosed ? (
          normallyClosedMessage
        ) : hasActiveSchedules && scheduleView === 'timeline' ? (
          <ScheduleTimeline
            schedules={activeSchedules}
            user={user}
            dimmerAvailable={dimmerAvailable}
            onClickEditSchedule={onClickEditSchedule}
            nextSunrise={next_sunrise}
            nextSunset={next_sunset}
            dayOfWeekScheduling={dayOfWeekScheduling}
          />
        ) : hasSchedules ? (
          scheduleList
        ) : (
          noSchedules
        )}
      </List>
    );
  }
}

export default ScheduleList;
